/**
 * Renders a sample/static result page for an assessment.
 * Uses `config.sampleReport` data instead of fetching a real submission from the DB.
 * Route: /sample/:slug
 */
import { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { fetchAssessment } from './api';
import { applyBranding } from './theme';
import { BrandProvider } from './components/BrandContext';
import { PhaseProvider } from './components/PhaseContext';
import type { Assessment, SubmissionScores, Answers, PageStyle } from './types';
import Result from './screens/Result';
import BrokenPage from './components/BrokenPage';

interface SampleReport {
  resultKey: string;
  scores: SubmissionScores;
  answers?: Answers;
}

export default function SampleResultPage() {
  const { slug = '' } = useParams();
  const [searchParams] = useSearchParams();
  const isPreview = searchParams.get('preview') !== '0'; // default to preview mode
  const [state, setState] = useState<
    | { phase: 'loading' }
    | { phase: 'error'; error: string }
    | { phase: 'ready'; assessment: Assessment; sample: SampleReport }
  >({ phase: 'loading' });

  useEffect(() => {
    let alive = true;
    fetchAssessment(slug)
      .then((assessment) => {
        if (!alive) return;
        const sample = (assessment.config as any).sampleReport as SampleReport | undefined;
        if (!sample) {
          setState({ phase: 'error', error: 'No sample report configured for this assessment.' });
          return;
        }
        // Ensure scores.result is set
        if (!sample.scores.result && sample.resultKey) {
          sample.scores.result = sample.resultKey;
        }
        setState({ phase: 'ready', assessment, sample });
      })
      .catch((e) => { if (alive) setState({ phase: 'error', error: e.message }); });
    return () => { alive = false; };
  }, [slug]);

  useEffect(() => {
    if (state.phase === 'ready') {
      applyBranding(state.assessment.config.branding, { brand: state.assessment.brand ?? null });
      document.title = `${state.assessment.title} — Sample Results`;
    }
  }, [state]);

  // Page-level background-image override only. Body bg color/gradient are
  // theme-owned (see App.tsx pageStyle effect comment).
  useEffect(() => {
    if (state.phase !== 'ready') return;
    const resultCopy = state.assessment.config.results?.[state.sample.resultKey];
    const pageStyle: PageStyle | undefined = resultCopy?.pageStyle;
    const body = document.body;
    if (pageStyle?.backgroundImageUrl && !pageStyle.backgroundGradient) {
      body.style.backgroundImage = `url("${pageStyle.backgroundImageUrl}")`;
    }
    return () => {
      body.style.backgroundImage = '';
    };
  }, [state]);

  if (state.phase === 'loading') {
    return <div className="loading-screen">Loading sample results...</div>;
  }
  if (state.phase === 'error') {
    const isNotFound = /not found|404|no sample/i.test(state.error || '');
    return (
      <BrokenPage
        variant={isNotFound ? '404' : 'error'}
        message={isNotFound
          ? "We don't have a sample report for this assessment yet. Tell us what you were trying to see and we'll point you to the right page."
          : undefined}
      />
    );
  }

  const { assessment, sample } = state;
  const branding = assessment.config.branding;
  const effectiveLogoUrl = assessment.brand?.logoUrl ?? branding?.logoUrl ?? null;
  const logoLinkUrl = assessment.brand?.logoLinkUrl ?? null;
  const showLogo = assessment.config.intro.showLogo !== false && !!effectiveLogoUrl;

  return (
    <BrandProvider brand={assessment.brand ?? null}>
    <PhaseProvider phase="result">
    <div className="app-shell">
      {showLogo && (
        <div className="brand-bar">
          {logoLinkUrl ? (
            <a href={logoLinkUrl} className="brand-bar-link" aria-label={assessment.brand?.name ?? 'Home'}>
              <img src={effectiveLogoUrl!} alt={assessment.brand?.name ?? ''} />
            </a>
          ) : (
            <img src={effectiveLogoUrl!} alt={assessment.brand?.name ?? ''} />
          )}
        </div>
      )}
      <Result
        assessment={assessment}
        scores={sample.scores}
        submissionId="sample"
        answers={sample.answers}
        preview={isPreview}
      />
    </div>
    </PhaseProvider>
    </BrandProvider>
  );
}
